import { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";

const photos: { src: string; alt: string }[] = [
  { src: "/photos/interior.webp", alt: "Интерьер ресторана Паруса на крыше — уютный зал с панорамными окнами" },
  { src: "/photos/view_night.webp", alt: "Ночной Петербург с крыши ресторана Паруса" },
  { src: "/photos/view_day.webp", alt: "Дневная панорама Санкт-Петербурга с крыши ресторана" },
  { src: "/photos/view_clouds.webp", alt: "Облачное небо над Петербургом — вид с террасы ресторана" },
  { src: "/photos/view_summer.webp", alt: "Летняя открытая терраса ресторана Паруса на крыше" },
  { src: "/photos/view_sunset.jpg", alt: "Закат над крышами Петербурга с видовой террасы ресторана" },
  { src: "/photos/food.jpg", alt: "Авторская кухня ресторана Паруса на крыше" },
  { src: "/photos/terrace.jpg", alt: "Открытая терраса ресторана Паруса на крыше с видом на Петербург" },
];

export default function Interior() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useSeo({
    title: "Интерьер и фото — Паруса на крыше | Вид на Петербург с крыши",
    description: "Фотогалерея ресторана Паруса на крыше: панорамный вид 300° на крыши Санкт-Петербурга, закаты над городом, открытая терраса, застеклённый зал с живыми растениями.",
    keywords: "фото ресторан на крыше Петербург, интерьер Паруса на крыше, панорамный вид крыша СПб, терраса ресторан Петербург, вид на город с крыши",
    image: "/photos/view_sunset.jpg",
    canonical: "/interior",
  });
  useBreadcrumbs([{ name: "Интерьер", path: "/interior" }]);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
  }, []);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto";
  };

  return (
    <div className="w-full min-h-screen bg-background relative z-10 pt-[100px] md:pt-[120px] pb-32 overflow-x-hidden">
      <div className="container mx-auto px-6 md:px-14 max-w-7xl relative">
        <div className="hidden sm:block absolute top-0 right-0 font-serif text-[130px] md:text-[240px] font-light leading-none text-primary/5 select-none pointer-events-none tracking-tighter -mt-20">Виды</div>

        <div className="mb-14 md:mb-20 relative z-10" style={{
          opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(16px)", transition: "opacity 0.5s ease, transform 0.5s ease"
        }}>
          <h1 className="text-4xl md:text-7xl font-serif font-light text-primary mb-6 tracking-wide">Пространство</h1>
          <div className="w-24 h-[1px] bg-primary/30"></div>
        </div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 md:gap-8 space-y-6 md:space-y-8 relative z-10" style={{
          opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(16px)", transition: "opacity 0.5s ease 0.1s, transform 0.5s ease 0.1s"
        }}>
          {photos.map((photo, index) => (
            <div 
              key={index} 
              className="break-inside-avoid relative overflow-hidden group cursor-pointer border border-primary/10"
              onClick={() => openLightbox(index)}
            >
              <img 
                src={photo.src} 
                alt={photo.alt} 
                className="w-full h-auto object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-background/20 group-hover:bg-transparent transition-colors duration-500" />
            </div>
          ))}
        </div>
      </div>

      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-[100] bg-background/98 backdrop-blur-xl flex items-center justify-center animate-in fade-in duration-300"
          onClick={closeLightbox}
        >
          <button className="absolute top-8 right-8 text-muted-foreground hover:text-accent transition-colors text-[10px] tracking-[0.3em] uppercase p-4" onClick={closeLightbox}>
            Закрыть [x]
          </button>
          
          <button className="absolute left-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-accent p-4 transition-colors font-serif text-4xl font-light" onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length); }}>
            &larr;
          </button>

          <img 
            src={photos[currentIndex].src} 
            alt={photos[currentIndex].alt} 
            className="max-h-[85vh] max-w-[85vw] object-contain border border-primary/20 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            loading="lazy"
          />

          <button className="absolute right-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-accent p-4 transition-colors font-serif text-4xl font-light" onClick={(e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev + 1) % photos.length); }}>
            &rarr;
          </button>
          
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-accent/50 tracking-[0.3em] text-[10px] uppercase">
            0{currentIndex + 1} / 0{photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
